import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { requireSessionUser } from '@/integrations/supabase/session';
import { toast } from '@/hooks/use-toast';
import type { LeadActivity } from '@/types/leads';

export function useLeadActivities(leadId: string | null) {
  return useQuery({
    queryKey: ['lead-activities', leadId],
    queryFn: async () => {
      if (!leadId) return [];

      const { data, error } = await supabase
        .from('lead_activities')
        .select(`
          *,
          profiles(full_name)
        `)
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as (LeadActivity & { profiles: { full_name: string } })[];
    },
    enabled: !!leadId,
  });
}

export function useAddLeadActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      activityType,
      description,
      metadata,
    }: {
      leadId: string;
      activityType: LeadActivity['activity_type'];
      description: string;
      metadata?: Record<string, any>;
    }) => {
      const user = await requireSessionUser();

      // Insert the activity
      const { error } = await supabase.from('lead_activities').insert({
        lead_id: leadId,
        user_id: user.id,
        activity_type: activityType,
        description,
        metadata: metadata || null,
      } as any);

      if (error) throw error;

      // Auto-assign: if the lead is currently unassigned, claim it for the acting user
      const { data: lead } = await supabase
        .from('leads')
        .select('assigned_csr_id')
        .eq('id', leadId)
        .single();

      if (lead && lead.assigned_csr_id == null) {
        await supabase
          .from('leads')
          .update({ assigned_csr_id: user.id, updated_at: new Date().toISOString() })
          .eq('id', leadId)
          .is('assigned_csr_id', null); // Guard against race conditions

        // Log the auto-assignment as a separate activity
        await supabase.from('lead_activities').insert({
          lead_id: leadId,
          user_id: user.id,
          activity_type: 'assignment',
          description: `Lead auto-assigned to you after logging a ${activityType}`,
          metadata: { auto_assigned: true, trigger_activity_type: activityType },
        } as any);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead-activities', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      toast({ title: 'Activity added' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add activity',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
